import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { forkJoin } from 'rxjs';
import { User } from '../../shared/classes/user';
import { Utilities } from '../../shared/utilities';
import { Settings } from '../../shared/settings';
import { Team } from '../../shared/classes/team';
import { PreferenceService } from '../../shared/services/preference.service';
import { TeamService } from '../../shared/services/team.service';
import { UserService } from '../../shared/services/user.service';
import {Preference} from '../../shared/classes/preference';

@Component({
  selector: 'app-sign-up-form',
  templateUrl: './sign-up-form.component.html',
  styleUrls: ['./sign-up-form.component.css']
})

export class SignUpFormComponent implements OnInit, OnDestroy {
  @Input() isInPopup = false;
  @Output() changeScreen: EventEmitter<any> = new EventEmitter<any>();

  public loading = true;
  public processing = false;
  public user = new User();
  public passwordConfirm = '';
  public error = '';
  public message = '';
  public teams: Team [] = [];
  public selectedFavouriteTeam: Team;
  public step = 1;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private preferenceService: PreferenceService,
    private teamService: TeamService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.resetFormData();
    // Si un usuario logueado entra al registro por URL, cierro sesión (vuelve a la home).
    if (!this.isInPopup && this.authenticationService.userIsLoggedIn()) {
      this.authenticationService.logout();
    } else {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
  }

  loadData(): void {
    const that = this;
    that.loading = true;
    that.teams = [];
    forkJoin([
      that.preferenceService.getPreferences(),
      that.teamService.getTeams()
    ]).subscribe(([getPreferencesResponse, getTeamsResponse]) => {
        if (getPreferencesResponse && getPreferencesResponse.Preferencias && getPreferencesResponse.Preferencias.length > 0) {
          getPreferencesResponse.Preferencias.forEach(
            onePreference => {
              const preference = Preference.parse(onePreference);
              that.user.UserPreferences.push(preference);
            }
          );
        }
        that.teams.push(
          Team.parse({
            TeamId: '',
            TeamName: 'Ninguno'
          })
        );
        if (getTeamsResponse && getTeamsResponse.Equipos && getTeamsResponse.Equipos.length > 0) {
          getTeamsResponse.Equipos.forEach(
            oneTeam => {
              const team = Team.parse(oneTeam);
              that.teams.push(team);
            }
          );
        }
        // tslint:disable-next-line:max-line-length
        that.selectedFavouriteTeam = that.teams.find(t => that.user.UserFavouriteTeam && that.user.UserFavouriteTeam.TeamId === t.TeamId);
        that.loading = false;
      },
      (error) => {
        Utilities.logError('Error al cargar los datos para sign-up.component');
        Utilities.logError(error);
        that.loading = false;
        that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
      });
  }

  public previousStep(): void {
    const that = this;
    that.error = '';
    that.message = '';
    that.step--;
    that.processing = false;
  }

  public nextStep(): void {
    const that = this;
    if (that.step === 3) {
      that.register();
    } else {
      that.step++;
      that.processing = false;
    }
  }

  public changeStep(type: string): void {
    const that = this;
    if (!that.processing) {
      that.processing = true;
      if (type === 'INCREASE') {
        if (that.validateData()) {
          that.nextStep();
        } else {
          that.processing = false;
        }
      } else if (type === 'DECREASE') {
        that.previousStep();
      }
    }
  }

  public register(): void {
    const that = this;
    forkJoin([
      that.userService.createUser(that.user)
    ]).subscribe(([createUserResponse]) => {
        if (createUserResponse) {
          if (!createUserResponse.HasError) {
            that.resetFormData();
            that.message = '<p><b>El usuario ha sido creado correctamente.</b></p>';
            that.message += '<p>Se le ha enviado un correo para activar el nuevo usuario.</p>';
            that.message += '<p>En caso de no encontrar el mensaje, no olvide revisar su carpeta de spam.</p>';
            if (that.isInPopup) {
              that.goToSignIn(that.message);
            }
          } else {
            if (createUserResponse.ErrorMessage){
              that.error = `<p><b>¡Error!</b></p><p>${createUserResponse.ErrorMessage}</p>`;
            } else {
              that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
            }
          }
        } else {
          that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
        }
        that.processing = false;
      },
      (error) => {
        Utilities.logError('Error al realizar el registro');
        Utilities.logError(error);
        that.processing = false;
        localStorage.clear();
        that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
      });
  }

  public validateData(): boolean {
    const that = this;
    that.error = '';
    that.message = '';
    let dataIsValid = true;
    if (that.step === 1) {
      if (
        that.user.UserFirstName &&
        that.user.UserFirstName.trim().length > 0 &&
        that.user.UserLastName &&
        that.user.UserLastName.trim().length > 0 &&
        that.user.UserEmail &&
        that.user.UserEmail.trim().length > 0 &&
        that.user.UserBirthDate &&
        that.user.UserBirthDate.trim().length > 0
      // De momento, el número de teléfono no es obligatorio
      /*that.user.UserPhoneNumber &&
      that.user.UserPhoneNumber.trim().length > 0 &&*/
      ) {
        that.user.UserFirstName = that.user.UserFirstName.trim();
        that.user.UserLastName = that.user.UserLastName.trim();
        that.user.UserEmail = that.user.UserEmail.trim();
        that.user.UserBirthDate = that.user.UserBirthDate.trim();
        that.user.UserPhoneNumber = that.user.UserPhoneNumber.trim();
        if (that.user.UserFirstName.length < 2) {
          dataIsValid = false;
          that.error += '<p>El campo de nombres debe contener al menos 2 caracteres.</p>';
        }
        if (that.user.UserLastName.length < 2) {
          dataIsValid = false;
          that.error += '<p>El campo de apellidos debe contener al menos 2 caracteres.</p>';
        }
        if (!that.user.UserEmail.match(Utilities.EXP_REG_FORMATO_EMAIL)) {
          dataIsValid = false;
          that.error += '<p>El email no tiene un formato válido.</p>';
        }
        const dateValidation = Utilities.dateValidation(that.user.UserBirthDate, 'fecha de nacimiento', '1900-01-01');
        if (!dateValidation.dateIsValid) {
          dataIsValid = false;
          dateValidation.errors.forEach(error => that.error += `<p>${error}</p>`);
        }
        if (!dataIsValid) {
          that.error = '<p><b>Por favor, solucione los errores y vuelva a intentar.</b></p>' + that.error;
        }
      } else {
        dataIsValid = false;
        that.error += '<p><b>Complete el formulario.</b></p><p>Todos los campos son obligatorios.</p>';
      }
    } else if (that.step === 2) {

    } else if (that.step === 3) {
      if (
        that.user.UserPassword &&
        that.user.UserPassword.trim().length > 0 &&
        that.passwordConfirm &&
        that.passwordConfirm.trim().length > 0
      ) {
        that.user.UserPassword = that.user.UserPassword.trim();
        that.passwordConfirm = that.passwordConfirm.trim();
        if (that.user.UserPassword !== that.passwordConfirm) {
          dataIsValid = false;
          that.error += '<p>La contraseña y la verificación deben ser iguales.</p>';
        }
        if (!dataIsValid) {
          that.error = '<p><b>Por favor, solucione los errores y vuelva a intentar.</b></p>' + that.error;
        }
      } else {
        dataIsValid = false;
        that.error += '<p><b>Complete el formulario.</b></p><p>Todos los campos son obligatorios.</p>';
      }
    }
    return dataIsValid;
  }

  private resetFormData(): void {
    this.user = new User();
    this.passwordConfirm = '';
    this.error = '';
    this.message = '';
  }

  public goToSignIn(message = ''): void {
    if (this.isInPopup) {
      this.changeScreen.emit({screen: 'SIGN_IN', message});
    } else {
      this.goTo('ingreso');
    }
  }

  public goTo(url): void {
    this.router.navigate([url]);
  }
}
